'use client'

import Master from '@components/Layout/Master'
import Section from '@components/Section/Section'
import { ScrollAnimations } from '../home/components/ScrollAnimations'
import { useLanguage } from '@contexts/LanguageContext'
import { type TranslationKey } from '@utils/translations'

interface IKeyStat {
  icon: string
  number?: string
  numberKey?: TranslationKey // for numbers whose wording differs per language
  label: TranslationKey
  desc: TranslationKey
}

const KEY_STATS: IKeyStat[] = [
  { icon: '🎓', number: '216+', label: 'impactStudentsLabel', desc: 'impactStudentsDesc' },
  { icon: '📚', number: '120+', label: 'impactBooksLabel', desc: 'impactBooksDesc' },
  { icon: '👨‍🏫', number: '20', label: 'impactTeachersLabel', desc: 'impactTeachersDesc' },
  { icon: '🏫', number: '2', label: 'impactSchoolsLabel', desc: 'impactSchoolsDesc' },
  { icon: '🌍', number: '1', label: 'impactRegionLabel', desc: 'impactRegionDesc' },
  {
    icon: '💰',
    numberKey: 'impactFundsAmount',
    label: 'impactFundsLabel',
    desc: 'impactFundsDesc',
  },
]

const FOCUS_AREAS: {
  icon: string
  title: TranslationKey
  desc: TranslationKey
  stat: TranslationKey
}[] = [
  {
    icon: '📚',
    title: 'literacyDevelopment',
    desc: 'literacyDevelopmentDesc',
    stat: 'literacyStudentsCount',
  },
  {
    icon: '🔢',
    title: 'numeracySkills',
    desc: 'numeracySkillsDesc',
    stat: 'numeracyStudentsCount',
  },
  {
    icon: '👨‍🏫',
    title: 'teacherTrainingImpact',
    desc: 'teacherTrainingImpactDesc',
    stat: 'teacherTrainingCount',
  },
]

const TESTIMONIALS: { quote: TranslationKey; author: TranslationKey }[] = [
  { quote: 'parentTestimonial', author: 'parentName' },
  { quote: 'teacherTestimonial', author: 'teacherName' },
  { quote: 'principalTestimonial', author: 'principalName' },
]

const MEASUREMENT_STEPS: { icon: string; title: TranslationKey; desc: TranslationKey }[] = [
  { icon: '📊', title: 'baselineAssessments', desc: 'baselineAssessmentsDesc' },
  { icon: '📈', title: 'ongoingTracking', desc: 'ongoingTrackingDesc' },
  { icon: '🎯', title: 'finalAssessment', desc: 'finalAssessmentDesc' },
  { icon: '📝', title: 'transparentReportingImpact', desc: 'transparentReportingImpactDesc' },
]

const LEARNING_GAINS: { label: TranslationKey; value: number }[] = [
  { label: 'literacyGain', value: 85 },
  { label: 'numeracyGain', value: 78 },
]

const ImpactPage: React.FC = () => {
  const { t } = useLanguage()

  return (
    <Master>
      <ScrollAnimations />

      {/* Hero Section */}
      <section className="impact-hero">
        <div className="container">
          <div className="impact-hero-content">
            <h1 className="impact-title">{t('measuringOurImpact')}</h1>
            <p className="impact-subtitle">{t('impactPageDesc')}</p>
          </div>
        </div>
      </section>

      {/* Key Stats Section */}
      <section className="impact-stats-section">
        <div className="container">
          <div className="section-header-center">
            <h2 className="section-title-large">{t('impactByNumbers')}</h2>
          </div>
          <div className="stats-grid-large">
            {KEY_STATS.map((stat) => (
              <div key={stat.label} className="stat-card-large">
                <div className="stat-icon">{stat.icon}</div>
                <div className={`stat-number ${stat.numberKey ? 'stat-number-long' : ''}`}>
                  {stat.numberKey ? t(stat.numberKey) : stat.number}
                </div>
                <div className="stat-label">{t(stat.label)}</div>
                <div className="stat-description">{t(stat.desc)}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Impact Focus Areas */}
      <Section className="impact-categories-section">
        <div className="container">
          <div className="section-header-center">
            <h2 className="section-title-large">{t('whereWeCreateImpact')}</h2>
            <p className="section-description">{t('focusAreasDesc')}</p>
          </div>

          <div className="impact-categories-grid impact-categories-grid-3">
            {FOCUS_AREAS.map((area) => (
              <div key={area.title} className="category-card">
                <div className="category-icon">{area.icon}</div>
                <h3>{t(area.title)}</h3>
                <p>{t(area.desc)}</p>
                <div className="category-stat">{t(area.stat)}</div>
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* Regional Impact Section */}
      <section className="regional-impact-section">
        <div className="container">
          <div className="section-header-center">
            <h2 className="section-title-large">{t('communitiesWeServe')}</h2>
            <p className="section-description">{t('communitiesWeServeDesc')}</p>
          </div>

          <div className="regions-grid regions-grid-single">
            <div className="region-card">
              <div className="region-name">{t('papuaWestPapua')}</div>
              <div className="region-stats">
                <div className="region-stat-item">
                  <span className="stat-value">216</span>
                  <span className="stat-label">{t('studentsCount')}</span>
                </div>
                <div className="region-stat-item">
                  <span className="stat-value">2</span>
                  <span className="stat-label">{t('schoolsCount')}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stories of Change */}
      <Section className="success-stories-section">
        <div className="container">
          <div className="section-header-center">
            <h2 className="section-title-large">{t('storiesOfChange')}</h2>
            <p className="section-description">{t('storiesOfChangeDesc')}</p>
          </div>

          <div className="stories-grid">
            {TESTIMONIALS.map((story) => (
              <div key={story.author} className="story-card">
                <div className="story-quote">&ldquo;</div>
                <p className="story-text">{t(story.quote)}</p>
                <div className="story-author">
                  <span>{t(story.author)}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* How We Measure Impact */}
      <section className="methodology-section">
        <div className="container">
          <div className="methodology-content">
            <div className="methodology-text">
              <h2 className="section-title-large">{t('howWeMeasureImpact')}</h2>
              <p className="methodology-description">{t('howWeMeasureDesc')}</p>

              <div className="methodology-list">
                {MEASUREMENT_STEPS.map((step) => (
                  <div key={step.title} className="methodology-item">
                    <div className="method-icon">{step.icon}</div>
                    <div className="method-content">
                      <h4>{t(step.title)}</h4>
                      <p>{t(step.desc)}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="methodology-visual">
              <div className="visual-card">
                <h3>{t('averageLearningGains')}</h3>
                {LEARNING_GAINS.map((gain) => (
                  <div key={gain.label} className="gain-item">
                    <span>{t(gain.label)}</span>
                    <div className="gain-bar">
                      <div className="gain-fill" style={{ width: `${gain.value}%` }}></div>
                    </div>
                    <strong>{gain.value}%</strong>
                  </div>
                ))}
                <p className="gain-note">{t('learningBenchmarkDesc')}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="impact-cta-section">
        <div className="container">
          <div className="cta-card-impact">
            <h2>{t('bePartOfImpact')}</h2>
            <p>{t('bePartOfImpactDesc')}</p>
            <div className="cta-buttons-group">
              <a href="/donate" className="btn-cta-primary-large">
                {t('makeADonation')}
              </a>
              <a href="/projects" className="btn-cta-secondary-large">
                {t('browsePrograms')}
              </a>
            </div>
          </div>
        </div>
      </section>
    </Master>
  )
}

export default ImpactPage
